import { type KeyboardEvent, type ReactNode, useId, useState } from 'react'

interface InfoTipProps {
  /** The icon's accessible name, such as "About visibility". */
  label: string
  children: ReactNode
  testId?: string
}

/**
 * A small "i" that explains the thing beside it. It opens on hover, focus or
 * a tap, since a phone has no hover, and closes when the pointer or focus
 * leaves, or on Escape. The note is the button's description either way, so a
 * screen reader hears it without opening anything.
 *
 * The note hangs from the nearest positioned ancestor, not the icon, so the
 * caller can start it at the label's edge, where it fits a phone's width.
 */
export function InfoTip({ label, children, testId }: InfoTipProps) {
  const [open, setOpen] = useState(false)
  const id = useId()

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'Escape' || !open) return
    event.preventDefault()
    setOpen(false)
  }

  return (
    <span className="inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // Opens rather than toggles: a click focuses first, and toggling would
        // close what the focus just opened.
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="-my-1 inline-flex size-6 items-center justify-center rounded-full text-ink-soft outline-none transition hover:text-brand focus-visible:ring-2 focus-visible:ring-brand-ring"
        data-testid={testId}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="none">
          <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 7.25v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="4.75" r="0.9" fill="currentColor" />
        </svg>
      </button>
      <span
        role="tooltip"
        id={id}
        hidden={!open}
        className="absolute top-full left-0 z-10 mt-1 w-64 max-w-[calc(100vw-6rem)] rounded-field bg-ink px-3 py-2 text-sm font-normal text-white shadow-xl"
        data-testid={testId && `${testId}-text`}
      >
        {children}
      </span>
    </span>
  )
}
